import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPartnerProduct,
  getProductSerialsPartner,
  updateProductStatusPartner,
  postNewCommentPartner,
  getSpecialPhrasePartner,
} from "../../../components/products/functions";
import {
  disablePartnerStatusList,
  getNextPartnerStatus,
  partnerStatusObject,
  statusFlow,
} from "../../../components/utils/allProductStatus";
import {
  getGradingTypeLabel,
  getScoreTitle,
  priorityTypes,
} from "../../../components/utils/gradingTypes";
import {
  getVisibleGradeFields,
  renderApplicableGradeValue,
  validateProductAutographNotes,
} from "../../../components/utils/serialGradeFields";
import {
  cashnator,
  cashnatorFloat,
  formatarData,
  formatarDataCompleta,
  cpfMask,
  phoneMask,
} from "../../../components/utils/functions";
import ToastContext from "../../../context/toastProvider";
import MobilePage from "../../ui/MobilePage";
import MobileSection from "../../ui/MobileSection";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileError from "../../ui/MobileError";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileFilterSheet from "../../ui/MobileFilterSheet";
import MobilePartnerProductEditSheet from "./MobilePartnerProductEditSheet";

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return (
    <div className="flex justify-between gap-4 border-b border-primary/20 py-2 text-sm last:border-0">
      <span className="text-laranjinha/60">{label}</span>
      <span className="text-right text-laranjinha">{value}</span>
    </div>
  );
}

const actionBtnClass =
  "inline-flex min-h-[36px] items-center rounded-lg border border-primary/50 px-3 text-xs font-medium text-laranjinha/80 hover:bg-monao";

function isGradedOrLater(product) {
  if (!statusFlow?.[product?.delivery]) return false;
  const gradedIndex = Object.keys(statusFlow[product.delivery]).indexOf("GRADED");
  const currentIndex = Object.keys(statusFlow[product.delivery]).indexOf(product.status);
  return currentIndex >= gradedIndex;
}

export default function MobilePartnerProductDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goodToast, badToast } = useContext(ToastContext);

  const [product, setProduct] = useState(null);
  const [serials, setSerials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [notify, setNotify] = useState(true);
  const [statusBusy, setStatusBusy] = useState(false);

  const [newComment, setNewComment] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);

  const [phrase, setPhrase] = useState("");
  const [phraseBusy, setPhraseBusy] = useState(false);

  const loadSerials = useCallback(async () => {
    const data = await getProductSerialsPartner(user?.token, id);
    if (data?.type === "success") {
      setSerials(data.data ?? []);
    }
  }, [user?.token, id]);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError(false);

    const data = await getPartnerProduct(user?.token, id);
    if (data?.type !== "success") {
      setError(true);
      setLoading(false);
      return;
    }

    setProduct(data.data);
    setLoading(false);
    await loadSerials();
  }, [user?.token, id, loadSerials]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  const openStatusSheet = () => {
    const next = getNextPartnerStatus(product?.status);
    setStatus(next ?? product?.status ?? "");
    setNotify(true);
    setStatusSheetOpen(true);
  };

  const submitStatus = async () => {
    if (!product?._id || status === product.status) return;

    if (status === "DONE") {
      if (serials.length < product.numberOfCards) {
        badToast("Seriais não preenchidos!");
        return;
      }
      const autographNotesValidation = validateProductAutographNotes({
        ...product,
        serials,
      });
      if (!autographNotesValidation.ok) {
        const messages = [];
        if (autographNotesValidation.missingCount > 0) {
          messages.push(
            `${autographNotesValidation.missingCount} ${
              autographNotesValidation.missingCount === 1
                ? "serial está sem nota de autógrafo"
                : "seriais estão sem nota de autógrafo"
            }`
          );
        }
        if (autographNotesValidation.invalidCount > 0) {
          messages.push(
            `${autographNotesValidation.invalidCount} ${
              autographNotesValidation.invalidCount === 1
                ? "serial tem nota de autógrafo inválida"
                : "seriais têm nota de autógrafo inválida"
            }`
          );
        }
        badToast(
          `Revise as notas de autógrafo antes de finalizar o pedido: ${messages.join(" e ")}.`
        );
        return;
      }
    }

    setStatusBusy(true);
    const data = await updateProductStatusPartner(
      user?.token,
      product._id,
      status,
      notify,
      partnerStatusObject[status],
      user?.email
    );
    setStatusBusy(false);
    if (data?.type !== "success") {
      badToast(data?.message ?? "Erro ao atualizar status");
      return;
    }
    goodToast("Status do produto atualizado!");
    setStatusSheetOpen(false);
    await loadProduct();
  };

  const submitComment = async () => {
    if (!newComment.trim() || !product?._id) return;
    setCommentBusy(true);
    const data = await postNewCommentPartner(
      user?.token,
      product._id,
      newComment.trim()
    );
    setCommentBusy(false);
    if (data?.type !== "success") {
      badToast("Erro ao criar comentário");
      return;
    }
    goodToast("Comentário criado!");
    setNewComment("");
    await loadProduct();
  };

  const loadPhrase = async () => {
    if (!product?._id) return;
    setPhraseBusy(true);
    const data = await getSpecialPhrasePartner(user?.token, product._id);
    setPhraseBusy(false);
    if (data?.type !== "success") {
      badToast(data?.message ?? "Erro ao obter frase secreta");
      return;
    }
    setPhrase(data?.data ?? "");
  };

  const copyGradesToClipboard = () => {
    const gradesString = serials
      .map((ps) => {
        const visibleGrades = getVisibleGradeFields(ps, product);
        const gradeLines = [
          ...visibleGrades.subgrades.map(
            (field) => `${field.label}: ${renderApplicableGradeValue(field.value)}`
          ),
          ...(visibleGrades.autographNote
            ? [
                `${visibleGrades.autographNote.label}: ${renderApplicableGradeValue(visibleGrades.autographNote.value)}`,
              ]
            : []),
          ...(visibleGrades.generalScore
            ? [
                `Nota Final: ${renderApplicableGradeValue(visibleGrades.generalScore.value)} (${getScoreTitle(visibleGrades.generalScore.value) || "-"})`,
              ]
            : []),
        ];
        return `
${ps?.serialNumber}
${ps?.gradableModel?.name} ${ps?.gradableModel?.numbering}
${gradeLines.join("\n")}
---
`;
      })
      .join("");
    navigator.clipboard.writeText(gradesString);
    goodToast("Notas copiadas!");
  };

  if (loading) {
    return (
      <MobilePage title="Pedido" onBack={() => navigate("/partner/products")}>
        <div aria-label="Carregando pedido" role="status">
          <MobileSkeleton lines={6} />
        </div>
      </MobilePage>
    );
  }

  if (error || !product) {
    return (
      <MobilePage title="Pedido" onBack={() => navigate("/partner/products")}>
        <MobileError
          message="Erro ao obter dados do pedido."
          onRetry={loadProduct}
        />
      </MobilePage>
    );
  }

  const userData = product.user;
  const productComments = product.comments ?? [];
  const userComments = userData?.comments ?? [];
  const statusDisabled = disablePartnerStatusList.includes(product.status);
  const showCopyGrades = isGradedOrLater(product);

  return (
    <MobilePage title="Pedido" onBack={() => navigate("/partner/products")}>
      <MobileSection title="Status">
        <p className="text-lg font-semibold text-laranjinha">
          {partnerStatusObject[product.status] ?? product.status}
        </p>
        {!statusDisabled ? (
          <button
            type="button"
            onClick={openStatusSheet}
            className={`mt-2 ${actionBtnClass}`}
          >
            Alterar status
          </button>
        ) : null}
      </MobileSection>

      <MobileSection title="Usuário">
        <DetailRow label="Nome" value={userData?.name} />
        <DetailRow label="E-mail" value={userData?.email} />
        <DetailRow label="Telefone" value={phoneMask(userData?.phone)} />
        <DetailRow label="CPF" value={cpfMask(userData?.documentNumber)} />
      </MobileSection>

      <MobileSection title="Pedido">
        <DetailRow label="Tipo" value={getGradingTypeLabel(product.gradingType)} />
        <DetailRow
          label="Autógrafo"
          value={product.autographEvaluation ? "Sim" : "Não"}
        />
        <DetailRow label="Nº de itens" value={String(product.numberOfCards)} />
        <DetailRow label="Prioridade" value={priorityTypes[product.priority]} />
        <DetailRow label="Prazo" value={product.deadline} />
        <DetailRow label="Entrega" value={product.delivery} />
        <DetailRow label="Preço final" value={cashnator(product.finalPrice)} />
        <DetailRow label="Valor itens" value={cashnator(product.cardsValue)} />
        <DetailRow label="Taxa de manuseio" value={cashnator(product.insurance)} />
        {product.delivery === "correios" ? (
          <>
            <DetailRow label="Valor frete" value={cashnatorFloat(product.shipping)} />
            <DetailRow label="Tipo frete" value={product.shippingService} />
          </>
        ) : null}
        <DetailRow label="Criado em" value={formatarData(product.createdAt)} />
        {product.store?.name ? (
          <DetailRow label="Loja" value={product.store.name} />
        ) : null}
        {product.returnTrackingCode ? (
          <DetailRow label="Rastreio retorno" value={product.returnTrackingCode} />
        ) : null}
        <button
          type="button"
          onClick={() => setEditSheetOpen(true)}
          className={`mt-2 ${actionBtnClass}`}
        >
          Editar pedido
        </button>
      </MobileSection>

      <MobileSection title="Seriais">
        {showCopyGrades ? (
          <button
            type="button"
            onClick={copyGradesToClipboard}
            className={`mb-2 ${actionBtnClass}`}
          >
            Copiar notas
          </button>
        ) : null}
        {serials.length === 0 ? (
          <MobileEmpty
            title="Sem seriais"
            message="Nenhum serial cadastrado para este pedido."
          />
        ) : (
          <ul className="space-y-2">
            {serials.map((serial, i) => {
              const visibleGrades = getVisibleGradeFields(serial, product);
              return (
                <li
                  key={serial._id ?? i}
                  className="rounded-lg border border-primary/30 px-3 py-2 text-sm text-laranjinha"
                >
                  <p className="font-medium">{serial.serialNumber ?? serial._id}</p>
                  {serial.gradableModel?.name ? (
                    <p className="text-xs text-laranjinha/60">
                      {serial.gradableModel.name} {serial.gradableModel.numbering}
                    </p>
                  ) : null}
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-laranjinha/70">
                    {visibleGrades.generalScore ? (
                      <span>
                        {visibleGrades.generalScore.label}:{" "}
                        {renderApplicableGradeValue(visibleGrades.generalScore.value)}
                      </span>
                    ) : null}
                    {visibleGrades.autographNote ? (
                      <span>
                        {visibleGrades.autographNote.label}:{" "}
                        {renderApplicableGradeValue(visibleGrades.autographNote.value)}
                      </span>
                    ) : null}
                    {visibleGrades.subgrades.map((field) => (
                      <span key={field.key}>
                        {field.label}: {renderApplicableGradeValue(field.value)}
                      </span>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <p className="mt-2 text-xs text-laranjinha/60">
          Cadastrados: {serials.length}/{product.numberOfCards}
        </p>
      </MobileSection>

      <MobileSection title="Frase secreta">
        <button
          type="button"
          onClick={loadPhrase}
          disabled={
            phraseBusy ||
            phrase !== "" ||
            ["CART", "GENERATED", "PIX", "PROCESSED", "PENDING", "APPROVED", "CANCELED"].includes(
              product.status
            )
          }
          className={`${actionBtnClass} disabled:opacity-50`}
        >
          {phraseBusy ? "Carregando…" : "Ver frase secreta"}
        </button>
        {phrase ? (
          <p className="mt-2 text-center text-sm font-semibold text-primary">
            {phrase}
          </p>
        ) : null}
      </MobileSection>

      <MobileSection title="Comentários do pedido">
        {productComments.length === 0 ? (
          <p className="text-sm text-laranjinha/60">Nenhum comentário</p>
        ) : (
          <ul className="space-y-2">
            {productComments.map((c, i) => (
              <li
                key={i}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm text-laranjinha"
              >
                <p className="text-xs text-laranjinha/60">
                  {formatarDataCompleta(c?.date)}
                </p>
                <p>{c?.text}</p>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value.slice(0, 300))}
            placeholder="Novo comentário"
            className="min-h-[44px] flex-1 rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
          />
          <button
            type="button"
            onClick={submitComment}
            disabled={!newComment.trim() || commentBusy}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
          >
            Enviar
          </button>
        </div>
      </MobileSection>

      {userComments.length > 0 ? (
        <MobileSection title="Comentários do usuário">
          <ul className="space-y-2">
            {userComments.map((c, i) => (
              <li
                key={i}
                className="rounded-lg border border-primary/30 px-3 py-2 text-sm text-laranjinha"
              >
                <p className="text-xs text-laranjinha/60">
                  {formatarDataCompleta(c?.date)}
                </p>
                <p>{c?.text}</p>
              </li>
            ))}
          </ul>
        </MobileSection>
      ) : null}

      <MobileFilterSheet
        open={statusSheetOpen}
        onClose={() => setStatusSheetOpen(false)}
        title="Alterar status"
      >
        <div className="space-y-4">
          {getNextPartnerStatus(product.status) ? (
            <p className="text-sm text-laranjinha/70">
              Status atual:{" "}
              <strong>{partnerStatusObject[product.status]}</strong>. Próximo
              sugerido:{" "}
              <strong>
                {partnerStatusObject[getNextPartnerStatus(product.status)]}
              </strong>
              .
            </p>
          ) : (
            <p className="text-sm text-laranjinha/70">
              Status atual: {partnerStatusObject[product.status]}.
            </p>
          )}

          <label className="block text-sm font-medium text-laranjinha/80">
            Novo status
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 w-full min-h-[44px] rounded-lg border border-primary/50 bg-monao px-3 text-base text-laranjinha"
            >
              {Object.keys(partnerStatusObject).map((s) => (
                <option key={s} value={s}>
                  {partnerStatusObject[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex min-h-[44px] items-center gap-3 text-sm text-laranjinha/80">
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="h-5 w-5 rounded border-primary/50"
            />
            Notificar usuário
          </label>

          <button
            type="button"
            onClick={submitStatus}
            disabled={statusBusy || status === product.status}
            className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-primary px-4 font-medium text-mgs-bg hover:bg-[#b47c06] disabled:opacity-50"
          >
            {statusBusy ? "Salvando…" : "Confirmar"}
          </button>
        </div>
      </MobileFilterSheet>

      <MobilePartnerProductEditSheet
        open={editSheetOpen}
        onClose={() => setEditSheetOpen(false)}
        product={product}
        user={user}
        onSuccess={loadProduct}
        goodToast={goodToast}
        badToast={badToast}
      />
    </MobilePage>
  );
}

